/**
 * The browser's own fullscreen, for the whole console.
 *
 * Deliberately not the same thing as a pane's fullscreen. A tile maximises
 * *inside* the page — the top bar, the rail and the session list are all still
 * there, which is right when an operator wants one terminal bigger and wrong
 * when the console is the only thing on the screen. This is the second case: a
 * wall on a monitor nobody is browsing with, where the tab strip, the URL bar
 * and the bookmarks are three rows of chrome spent on nothing.
 *
 * The element is the document, not a region, so every overlay the console
 * portals to `document.body` — the shortcut sheet, the palette, a confirm — is
 * inside the fullscreen subtree. Fullscreening `#main` instead would have put
 * every one of them on the hidden side of the screen, which is the failure this
 * API is famous for.
 *
 * Three things this has to survive, and none of them are hypothetical:
 *
 *   - **The browser leaves on its own.** Esc exits, and so does switching tabs
 *     in some builds. Nothing tells the caller, so the state is read from
 *     `fullscreenElement` on every `fullscreenchange` rather than toggled
 *     locally — a local boolean says "fullscreen" over a window that is not.
 *   - **It can be refused.** `requestFullscreen` rejects when the gesture is not
 *     trusted or a policy forbids it, and an unhandled rejection is a console
 *     error nobody sees. It resolves to whether the request was honoured.
 *   - **Safari has its own spelling**, prefixed and callback-based, and iOS
 *     Safari has none at all on non-video elements — hence `supported`, so the
 *     control can be left out rather than shown and inert.
 *
 * ESCAPE BELONGS TO THE TERMINAL
 * ------------------------------
 * The first bullet above is also a defect once the console is fullscreen,
 * because **Esc is a key this product carries rather than a key it owns**: it is
 * how an operator interrupts an agent mid-turn — the panes say "esc to
 * interrupt" in so many words — and it closes the palette, the shortcut sheet
 * and a maximised tile. In fullscreen the browser takes it first, so the
 * gesture meant for the agent silently dropped the whole window out of
 * fullscreen instead. Reported 2026-09-19, one day after the fullscreen button
 * became the way to read a wall.
 *
 * `navigator.keyboard.lock(['Escape'])` is the API for exactly this, and the
 * conditions are already met here: a secure context, a real click on the
 * control, and *JavaScript-initiated* fullscreen — the lock has no effect on a
 * window the operating system put fullscreen, which is the other half of why
 * the console's own control is the one to press. Escape is then delivered to
 * the page, and the way out becomes press-and-hold, which Chromium says on
 * screen itself when the lock is taken. Nothing is added here to repeat it: an
 * operator is told once by the browser, and a banner of our own would be a row
 * of chrome spent saying what is already said.
 *
 * Only `Escape`. `lock()` with no argument claims *every* key including ⌘W and
 * ⌘T, which is a claim no console has any business making over the browser it
 * is a guest in.
 */

import { useCallback, useEffect, useState } from 'react'

/** The prefixed spellings, typed so the casts stay in one place. */
interface WebkitDocument extends Document {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => void
}

interface WebkitElement extends HTMLElement {
  webkitRequestFullscreen?: () => void
}

function current(): Element | null {
  const doc = document as WebkitDocument
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

/** Not in the DOM lib in every TypeScript version this repo has built under —
 *  the same reason `useWakeLock` declares its own. */
interface KeyboardLockLike {
  lock: (codes?: string[]) => Promise<void>
  unlock: () => void
}

function keyboard(): KeyboardLockLike | null {
  if (typeof navigator === 'undefined') return null
  const nav = navigator as Navigator & { keyboard?: KeyboardLockLike }
  const api = nav.keyboard
  return api && typeof api.lock === 'function' ? api : null
}

/** Claim Escape for the page, if this browser lends it.
 *
 *  Taken *before* the fullscreen request, which is what the specification asks
 *  for and is not a detail: locking afterwards means the browser has already
 *  told the operator that Esc leaves, and it would then have to correct itself.
 *
 *  Every failure is swallowed. Chromium has it, Safari has never shipped
 *  `navigator.keyboard` and Firefox is still implementing it; where the lock is
 *  refused the console behaves exactly as it did before — Esc leaves fullscreen
 *  — which is a worse terminal but not a broken console, and there is nothing an
 *  operator would do with the message. */
function lockEscape() {
  try {
    void keyboard()
      ?.lock(['Escape'])
      .catch(() => {})
  } catch {
    // `lock()` throws synchronously on an invalid key code or outside a
    // top-level browsing context — an iframe, which this console is not, but a
    // throw here would take the fullscreen request down with it.
  }
}

/** Give Escape back. Called on the way out rather than beside `exitFullscreen`,
 *  because the browser leaves fullscreen on its own — press-and-hold, a tab
 *  switch, a window-manager gesture — and a lock left behind would outlive the
 *  mode that justified it. */
function unlockEscape() {
  try {
    keyboard()?.unlock()
  } catch {
    // Nothing held it, or the browser objects. Either way there is no lock.
  }
}

export function useFullscreen() {
  const [full, setFull] = useState(() => current() !== null)

  useEffect(() => {
    const sync = () => {
      const held = current() !== null
      // Released from the event rather than from the toggle, for the reason the
      // state itself is read here: the browser leaves on its own and tells
      // nobody. This is the one place that learns about every exit.
      if (!held) unlockEscape()
      setFull(held)
    }
    // Both spellings: a browser that fires only the prefixed event would
    // otherwise show the button in the wrong state for as long as the tab lives.
    document.addEventListener('fullscreenchange', sync)
    document.addEventListener('webkitfullscreenchange', sync)
    return () => {
      document.removeEventListener('fullscreenchange', sync)
      document.removeEventListener('webkitfullscreenchange', sync)
      // The claim on Escape belongs to the console being on screen, not to the
      // document outliving it. Left held past an unmount it would be a key the
      // browser no longer routes anywhere anyone can see.
      unlockEscape()
    }
  }, [])

  const root = document.documentElement as WebkitElement
  const supported =
    typeof root.requestFullscreen === 'function' || typeof root.webkitRequestFullscreen === 'function'

  const toggle = useCallback(() => {
    const doc = document as WebkitDocument
    if (current()) {
      if (typeof document.exitFullscreen === 'function') void document.exitFullscreen().catch(() => {})
      else doc.webkitExitFullscreen?.()
      return
    }
    // Before the request, and unconditionally: a lock taken while the request
    // is then refused is released by the cleanup below and by the next exit
    // event, where a lock taken *after* a successful request would have let the
    // browser announce the wrong way out first.
    lockEscape()
    const el = document.documentElement as WebkitElement
    if (typeof el.requestFullscreen === 'function') {
      // Swallowed rather than surfaced: a refusal leaves the console exactly as
      // it was, the button stays in its "enter" state because the event never
      // fired, and there is nothing an operator would do with the message.
      //
      // Escape is handed back here too. A refusal fires no `fullscreenchange`,
      // so the release above never runs, and the claim would sit there for the
      // life of the page over a window that never went fullscreen.
      void el.requestFullscreen().catch(() => unlockEscape())
    } else {
      el.webkitRequestFullscreen?.()
    }
  }, [])

  return { full, toggle, supported }
}
